import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Bot } from 'lucide-react';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
} from 'chart.js';
import { Radar } from 'react-chartjs-2';
import {
  BarChart3,
  Target,
  Building2,
  Database,
  Users,
  Shield,
  Lightbulb,
  TrendingUp,
  Zap
} from 'lucide-react';
import ChatConsultor from './ChatConsultor';

// Configuración de API directa
const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:3001' 
  : (window.API_BASE_URL || process.env.REACT_APP_API_URL || '');


// Registrar los componentes necesarios para Chart.js
ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const ResultsPage = () => {
  const { submissionId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Calcular puntaje total y nivel de madurez
  const calculateTotalScore = (properties) => {
    if (!properties) return { totalScore: 0, maxPossibleScore: 0, maturityLevel: 1, maturityDescription: '' };

    const questionGroups = {
      'Negocio': [
        'La organización tiene una visión formalizada sobre el rol estratégico de la IA en su crecimiento futuro.',
        'El liderazgo ejecutivo está activamente comprometido con el despliegue estratégico de IA.',
        'Existe un equipo o rol formalmente asignado para liderar iniciativas de IA.',
        'Se han priorizado áreas y procesos donde IA puede generar impacto medible.',
        'Se han definido y documentado casos de uso de IA alineados a los objetivos de negocio.',
        'Mi organización destina fondos/recursos a proyectos relacionados con la IA'
      ],
      'Tecnología': [
        'Existe un área o persona encargada de la estrategia de tecnología en la organización.',
        'Los procesos clave de la organización están digitalizados.',
        'Los sistemas actuales están integrados o cuentan con posibilidad de integrarse, y es fácil acceder a la información.',
        'La organización cuenta con un ERP central para la gestión administrativa y CRM para la gestión comercial.',
        'En la organización hay concientización de la ciberseguridad como un aspecto importante a tener en cuenta (protección de activos, evaluación de riesgos, control de accesos, etc).',
        'La empresa cuenta con servicios de nube (AWS, Google Cloud, Azure) para diferentes necesidades de negocio.'
      ],
      'Data': [
        'Los datos se utilizan en la organización para definir objetivos y hacer se rija por los datos (data driven).',
        'La organización utiliza los datos para resolver problemas relacionados con los productos y servicios ofrecidos.',
        'La organización dispone de la infraestructura necesaria para almacenar, gestionar, acceder y recuperar datos de forma eficaz.',
        'La calidad de los datos de mi organización es precisa y coherente.',
        'Los datos de mi organización están bien integrados y están a disposición de los empleados con los derechos de acceso adecuados.'
      ],
      'Equipos': [
        'Hay apertura a nuevas ideas o tecnologías.',
        'Uno o más miembros del equipo han mostrado interés en aprender sobre IA',
        'Se han identificado roles que podrían beneficiarse con herramientas inteligentes.',
        'Se ha incentivado la formación básica (videos, cursos, webinars) en temas de IA.',
        'Se comunican claramente los beneficios de la IA al equipo.',
        'Se aborda el miedo al reemplazo con capacitación y transparencia.'
      ],
      'Procesos': [
        'Las actividades principales de la empresa se rigen por procesos estandarizados.',
        'Las personas involucradas conocen sus responsabilidades dentro de cada proceso.',
        'Los procesos clave están documentados (manuales, diagramas, SOPs).',
        'Existen indicadores de desempeño de procesos (KPIs Operativos).',
        'Se trabaja en identificar y automatizar procesos repetitivos de manera continua.',
        'Los procesos actuales soportarían un crecimiento significativo en volumen o complejidad.'
      ],
      'Aplicaciones iniciales': [
        'Asistentes de contenido:¿Se utilizan herramientas de IA para redactar, corregir o generar contenidos (por ejemplo, para blogs, redes sociales, presentaciones)? (Ej: ChatGPT, Jasper, Copy.ai)',
        'Chatbots y atención automática: ¿Se ha considerado implementar un chatbot en la web o redes sociales?',
        'Clasificación automática de correos o consultas:¿Se están explorando sistemas de IA que prioricen, clasifiquen o respondan correos electrónicos o solicitudes automáticamente?',
        'Análisis de datos de clientes:¿Hay interés en usar IA para analizar datos de ventas o comportamiento de clientes?',
        'Análisis de satisfacción del cliente:¿Se utilizan herramientas de IA para analizar encuestas, comentarios o reseñas de clientes y extraer insights sobre su satisfacción?',
        'Optimización de procesos internos:¿Se consideran herramientas de IA para detectar cuellos de botella o mejorar procesos administrativos o logísticos?',
        'Sistemas de recomendación de productos o servicios:¿Se explora el uso de IA para personalizar recomendaciones a clientes, basadas en su historial de compras o navegación?',
        'Detección de anomalías o fraudes:¿Se están considerando soluciones de IA para detectar transacciones inusuales, fraudes o riesgos operativos?',
        'Soporte en procesos de reclutamiento:¿Se evalúan herramientas de IA para ayudar en la preselección de candidatos o análisis de CVs?',
        'Automatización de tareas financieras básicas:¿Se utilizan sistemas de IA para conciliaciones contables, gestión de gastos o reportes automáticos?'
      ]
    };



    let totalScore = 0;
    let maxPossibleScore = 0;

    const getPropertyValue = (property) => {
      if (!property) return 'No disponible';

      if (property.type === 'rich_text' && property.rich_text) {
        return property.rich_text[0]?.plain_text || 'No disponible';
      }

      if (property.type === 'title' && property.title) {
        return property.title[0]?.plain_text || 'No disponible';
      }

      if (property.type === 'email' && property.email) {
        return property.email;
      }

      return 'No disponible';
    };

    const isScoreQuestion = (value, groupName) => {
      const numValue = parseInt(value);
      if (groupName === 'Aplicaciones iniciales') {
        return !isNaN(numValue) && numValue >= 1 && numValue <= 3;
      }
      return !isNaN(numValue) && numValue >= 1 && numValue <= 4;
    };

    console.log('🔢 Debugging cálculo de puntajes:');
    console.log('Question groups:', Object.keys(questionGroups));
    console.log('🔢 ¿Existe Aplicaciones iniciales?', 'Aplicaciones iniciales' in questionGroups);
    console.log('🔢 Contenido de Aplicaciones iniciales:', questionGroups['Aplicaciones iniciales']);

    Object.entries(questionGroups).forEach(([groupName, questions]) => {
      console.log(`📊 Grupo: ${groupName} - ${questions.length} preguntas`);
      if (groupName === 'Negocio') {
        console.log('🔍 Debugging específico para Negocio:');
        console.log('Properties keys disponibles:', Object.keys(properties));
      }
      questions.forEach(question => {
        const value = properties[question];
        if (value) {
          const score = getPropertyValue(value);
          if (isScoreQuestion(score, groupName)) {
            totalScore += parseInt(score);
            // Aplicaciones Iniciales tiene escala del 1 al 3, el resto del 1 al 4
            const maxScoreForQuestion = groupName === 'Aplicaciones iniciales' ? 3 : 4;
            maxPossibleScore += maxScoreForQuestion;
            console.log(`  ✅ Pregunta: ${question.substring(0, 50)}... - Score: ${score} - Max: ${maxScoreForQuestion}`);
          } else {
            console.log(`  ❌ Pregunta: ${question.substring(0, 50)}... - Score: ${score} (no válido)`);
          }
        } else {
          console.log(`  ⚠️ Pregunta no encontrada: ${question.substring(0, 50)}...`);
          if (groupName === 'Negocio') {
            console.log(`    🔍 Buscando pregunta similar para: "${question}"`);
            const similarKeys = Object.keys(properties).filter(key =>
              key.includes('visión') ||
              key.includes('liderazgo') ||
              key.includes('equipo') ||
              key.includes('priorizado') ||
              key.includes('casos de uso') ||
              key.includes('fondos')
            );
            console.log(`    🔍 Claves similares encontradas:`, similarKeys);
          }
        }
      });
    });

    console.log(`🎯 Total Score: ${totalScore}, Max Possible: ${maxPossibleScore}`);

    // Calcular nivel de madurez organizacional
    const getMaturityLevel = (score) => {
      if (score >= 1 && score <= 30) {
        return {
          level: 1,
          name: 'Exploración Inicial',
          description: 'La organización apenas comienza a familiarizarse con el potencial de la IA. Tiene bajo conocimiento general, sin estrategias claras ni capacidades técnicas o culturales para avanzar. Es clave sensibilizar, capacitar y generar visión común.'
        };
      } else if (score >= 31 && score <= 60) {
        return {
          level: 2,
          name: 'Compromiso Emergente',
          description: 'Existen algunas iniciativas puntuales o interés general, pero sin una estrategia integrada. Hay avances parciales en infraestructura y cultura, y se empiezan a explorar casos de uso piloto. Se requiere alineación estratégica y foco en capacidades básicas.'
        };
      } else if (score >= 61 && score <= 90) {
        return {
          level: 3,
          name: 'Integración Operativa',
          description: 'La IA empieza a integrarse en procesos con una visión estratégica en desarrollo. Se observa inversión en herramientas, prácticas de datos y cultura organizacional en transformación. Hay una base técnica y organizacional que permite escalar iniciativas.'
        };
      } else if (score >= 91 && score <= 120) {
        return {
          level: 4,
          name: 'Implementación Avanzada',
          description: 'La IA es parte activa de la estrategia de negocio. Existen equipos dedicados, procesos clara, inversión sostenida, y un marco ético en marcha. La organización innova, mide resultados, y mejora continuamente en todas las dimensiones.'
        };
      } else if (score >= 121 && score <= 146) {
        return {
          level: 5,
          name: 'Liderazgo Transformacional',
          description: 'La organización lidera en el uso estratégico y ético de la IA. La innovación, la cultura y la tecnología están profundamente integradas. Inspira a su industria, define benchmarks y aprovecha la IA para impulsar transformaciones sistémicas.'
        };
      } else {
        return {
          level: 1,
          name: 'Exploración Inicial',
          description: 'La organización apenas comienza a familiarizarse con el potencial de la IA. Tiene bajo conocimiento general, sin estrategias claras ni capacidades técnicas o culturales para avanzar. Es clave sensibilizar, capacitar y generar visión común.'
        };
      }
    };

    const maturityInfo = getMaturityLevel(totalScore);

    return {
      totalScore,
      maxPossibleScore,
      maturityLevel: maturityInfo.level,
      maturityName: maturityInfo.name,
      maturityDescription: maturityInfo.description
    };
  };



  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('Consultando API de Notion para submissionId:', submissionId);

        // Usar el servidor proxy para evitar CORS
        const response = await axios.post(`${API_BASE_URL}/api/notion/query`, {
          filter: {
            property: 'Submission ID',
            rich_text: { equals: submissionId }
          }
        });

        console.log('✅ Respuesta exitosa:', response.data);
        console.log('Número de resultados:', response.data.results?.length);

        if (response.data.results && response.data.results.length > 0) {
          console.log('✅ Datos encontrados, estableciendo estado...');
          setData(response.data.results[0]);
        } else {
          console.log('❌ No se encontraron resultados');
          setError('No se encontraron resultados para este Submission ID');
        }
      } catch (err) {
        console.error('❌ Error completo:', err);
        console.error('Error response:', err.response);
        console.error('Error message:', err.message);

        if (err.response) {
          console.error('Detalles del error:', {
            status: err.response.status,
            statusText: err.response.statusText,
            data: err.response.data
          });

          if (err.response.status === 401) {
            setError('Error de autenticación: Token de Notion inválido o expirado');
          } else if (err.response.status === 403) {
            setError('Error de permisos: No tienes acceso a esta base de datos');
          } else if (err.response.status === 404) {
            setError('Base de datos no encontrada');
          } else if (err.response.status === 0) {
            setError('Error de CORS: No se puede acceder a la API de Notion directamente desde el navegador. Usa un servidor proxy o una extensión de CORS.');
          } else {
            setError(`Error ${err.response.status}: ${err.response.data?.message || err.message}`);
          }
        } else if (err.request) {
          console.error('Error de red:', err.request);
          setError('Error de red: No se pudo conectar con la API de Notion. Verifica tu conexión a internet.');
        } else {
          console.error('Error de configuración:', err.message);
          setError(`Error: ${err.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [submissionId]);

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          Cargando resultados...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="error">
          <h2>Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container">
        <div className="error">
          <h2>No se encontraron datos</h2>
          <p>No se encontraron resultados para el Submission ID: {submissionId}</p>
        </div>
      </div>
    );
  }

  const { totalScore, maxPossibleScore, maturityLevel } = calculateTotalScore(data?.properties);

  return (
    <>
      <div className="header">
        <div className="container">
          <div className="header-content">
            <div className="header-left">
              <div className="logo-container">
                <img src="/logo-fliggo-blanco.svg" alt="Jemer" className="jemer-logo" />
              </div>
              <h1>AI Readiness Assessment</h1>
              <h2>Resultados</h2>
            </div>
            <div className="header-right">
            </div>
          </div>
        </div>
      </div>
      <div className="container">


        <ResultsDisplay
          data={data}
          totalScore={totalScore}
          maxPossibleScore={maxPossibleScore}
          maturityLevel={maturityLevel}
        />
      </div>
    </>
  );
};

const ResultsDisplay = ({ data, totalScore, maxPossibleScore, maturityLevel }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showConsultorModal, setShowConsultorModal] = useState(false);
  const properties = data.properties;

  // Prevenir scroll del body cuando el modal está abierto
  useEffect(() => {
    if (showConsultorModal) {
      // Guardar el scroll actual
      const scrollY = window.scrollY;
      // Prevenir scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      // Restaurar el scroll
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    // Cleanup function
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [showConsultorModal]);

  // Diagnósticos por pilar para cada nivel de madurez
  const diagnosticosPorNivel = {
    1: {
      'Negocio': 'La organización no tiene una visión clara sobre el rol de la IA en su estrategia. No hay liderazgo comprometido ni casos de uso definidos. Es fundamental comenzar con sensibilización y capacitación básica.',
      'Tecnología': 'No existe gestión estructurada de tecnología. Los sistemas son básicos y no integrados. Falta liderazgo digital y conciencia en ciberseguridad. Es clave definir responsables y modernizar.',
      'Data': 'No hay una cultura de uso de datos ni infraestructura para su gestión. Los datos están dispersos y sin calidad. Se requiere establecer procesos básicos de recopilación y almacenamiento.',
      'Equipos': 'Existe resistencia al cambio y desconocimiento sobre IA. No hay apertura a nuevas tecnologías ni interés en formación. Es clave trabajar en sensibilización y comunicación.',
      'Procesos': 'No existen procesos formalizados ni consistencia en la ejecución. Las tareas se hacen de forma improvisada. Es necesario identificar y estandarizar los procesos clave del negocio.',
      'Aplicaciones iniciales': 'No hay implementación de herramientas de IA en procesos específicos. La organización no utiliza chatbots, análisis de datos con IA, ni automatización inteligente. Es fundamental comenzar con pilotos simples y casos de uso básicos.'
    },
    2: {
      'Negocio': 'Existe interés general pero sin estrategia formalizada. Hay iniciativas aisladas sin alineación. Se requiere definir visión clara y priorizar casos de uso específicos.',
      'Tecnología': 'Hay avances iniciales en digitalización, pero sin estrategia ni integración real. Se usan herramientas aisladas y la seguridad es limitada. Se debe consolidar sistemas y políticas tecnológicas.',
      'Data': 'Se utilizan datos básicos pero sin estructura ni calidad garantizada. Falta integración y accesibilidad. Es necesario establecer procesos de gestión y calidad de datos.',
      'Equipos': 'Hay apertura inicial a nuevas tecnologías y algunos miembros muestran interés. Falta formación estructurada y comunicación de beneficios. Se requiere expandir la adopción.',
      'Procesos': 'Hay procesos básicos pero poco documentados y sin indicadores claros. Falta claridad en roles y responsabilidades. Se recomienda formalizar flujos y comenzar a medir desempeño.',
      'Aplicaciones iniciales': 'Existe interés en implementar herramientas de IA pero sin casos de uso definidos. Se exploran algunas aplicaciones básicas como chatbots o análisis de datos. Se requiere identificar procesos específicos y comenzar con pilotos controlados.'
    },
    3: {
      'Negocio': 'La IA no está aún integrada como parte de la estrategia institucional. No hay procesos priorizados, casos de uso definidos ni responsables asignados. Esto limita la posibilidad de orientar esfuerzos y recursos con foco.',
      'Tecnología': 'La organización cuenta con bases tecnológicas sólidas y cierto nivel de integración. Hay roles definidos y uso básico de la nube. Se requiere alinear la estrategia y fortalecer la interoperabilidad.',
      'Data': 'El uso de datos existe pero no está estructurado con calidad, integración ni accesibilidad garantizadas. El desarrollo de una gobernanza básica y la mejora en la trazabilidad serán fundamentales para evolucionar hacia usos más avanzados.',
      'Equipos': 'Hay apertura a la innovación tecnológica y disposición a formarse, pero aún falta integración transversal y comunicación clara sobre beneficios e impacto de la IA.',
      'Procesos': 'Los procesos principales están definidos, documentados y se siguen con cierta consistencia. Existen indicadores básicos. Se debe avanzar en medición, revisión y automatización.',
      'Aplicaciones iniciales': 'El uso de IA en procesos concretos es aún incipiente y disperso, pero existe un terreno fértil para pasar de pruebas aisladas a pilotos estratégicos con impacto medible.'
    },
    4: {
      'Negocio': 'La IA está integrada en la estrategia de negocio con casos de uso definidos y equipos dedicados. Hay procesos priorizados y medición de resultados. Se requiere optimizar y escalar iniciativas existentes.',
      'Tecnología': 'La gestión tecnológica está alineada con los objetivos del negocio. Los sistemas están integrados y seguros, con uso eficiente de la nube. El foco debe estar en optimización y escalabilidad.',
      'Data': 'Existe una gobernanza de datos madura con calidad, integración y accesibilidad garantizadas. Los datos se utilizan estratégicamente. Se puede avanzar hacia analytics avanzado y machine learning.',
      'Equipos': 'La cultura organizacional es proactiva hacia la innovación tecnológica. Hay formación continua y comunicación efectiva sobre IA. Se puede enfocar en especialización y liderazgo.',
      'Procesos': 'Los procesos se gestionan con KPIs y revisión continua. Hay eficiencia, automatización parcial y capacidad para escalar. El foco está en optimizar y asegurar la mejora continua.',
      'Aplicaciones iniciales': 'La IA está implementada en múltiples procesos con impacto medible. Hay pilotos exitosos y casos de uso escalados. Se puede avanzar hacia soluciones más complejas e innovadoras.'
    },
    5: {
      'Negocio': 'La IA es un pilar fundamental de la estrategia de negocio con visión de largo plazo. Hay liderazgo transformacional y casos de uso innovadores. La organización es referente en el sector.',
      'Tecnología': 'La tecnología es un habilitador estratégico del negocio. Existe liderazgo maduro, ecosistema digital integrado y fuerte cultura de seguridad. La organización innova y adopta tecnologías emergentes.',
      'Data': 'La gobernanza de datos es excelente con analytics avanzado y machine learning implementado. Los datos generan valor estratégico significativo. Se innova constantemente en este ámbito.',
      'Equipos': 'La cultura es completamente digital-first con innovación continua. Hay especialización avanzada y liderazgo en el sector. La organización atrae talento tecnológico.',
      'Procesos': 'Los procesos son ágiles, medidos y mejorados continuamente. Existe alta automatización y resiliencia ante el crecimiento. La mejora continua forma parte de la cultura organizacional.',
      'Aplicaciones iniciales': 'La IA está completamente integrada en todos los procesos críticos con resultados excepcionales. Se innova constantemente y se desarrollan soluciones propias. La organización es pionera.'
    }
  };


  // Función para extraer el valor de una propiedad
  const getPropertyValue = (property) => {
    if (!property) return 'No disponible';

    if (property.type === 'rich_text' && property.rich_text) {
      return property.rich_text[0]?.plain_text || 'No disponible';
    }

    if (property.type === 'title' && property.title) {
      return property.title[0]?.plain_text || 'No disponible';
    }

    if (property.type === 'email' && property.email) {
      return property.email;
    }

    return 'No disponible';
  };

  // Función para determinar si una pregunta es de puntuación (1-4 o 1-3 para Aplicaciones)
  const isScoreQuestion = (value, groupName) => {
    const numValue = parseInt(value);
    if (groupName === 'Aplicaciones iniciales') {
      return !isNaN(numValue) && numValue >= 1 && numValue <= 3;
    }
    return !isNaN(numValue) && numValue >= 1 && numValue <= 4;
  };

  // Función para obtener el color del score (usando colores de Jemer)
  const getScoreColor = (score) => {
    const numScore = parseInt(score);
    if (numScore === 1) return '#ff6b6b'; // Rojo
    if (numScore === 2) return '#ffa726'; // Naranja
    if (numScore === 3) return '#00D9C2'; // Verde Jemer
    if (numScore === 4) return '#00D9C2'; // Verde Jemer
    return '#00D9C2'; // Default Jemer
  };



  // Definir los grupos de preguntas
  const questionGroups = {
    'Negocio': [
      'La organización tiene una visión formalizada sobre el rol estratégico de la IA en su crecimiento futuro.',
      'El liderazgo ejecutivo está activamente comprometido con el despliegue estratégico de IA.',
      'Existe un equipo o rol formalmente asignado para liderar iniciativas de IA.',
      'Se han priorizado áreas y procesos donde IA puede generar impacto medible.',
      'Se han definido y documentado casos de uso de IA alineados a los objetivos de negocio.',
      'Mi organización destina fondos/recursos a proyectos relacionados con la IA'
    ],
    'Tecnología': [
      'Existe un área o persona encargada de la estrategia de tecnología en la organización.',
      'Los procesos clave de la organización están digitalizados.',
      'Los sistemas actuales están integrados o cuentan con posibilidad de integrarse, y es fácil acceder a la información.',
      'La organización cuenta con un ERP central para la gestión administrativa y CRM para la gestión comercial.',
      'En la organización hay concientización de la ciberseguridad como un aspecto importante a tener en cuenta (protección de activos, evaluación de riesgos, control de accesos, etc).',
      'La empresa cuenta con servicios de nube (AWS, Google Cloud, Azure) para diferentes necesidades de negocio.'
    ],
    'Data': [
      'Los datos se utilizan en la organización para definir objetivos y hacer se rija por los datos (data driven).',
      'La organización utiliza los datos para resolver problemas relacionados con los productos y servicios ofrecidos.',
      'La organización dispone de la infraestructura necesaria para almacenar, gestionar, acceder y recuperar datos de forma eficaz.',
      'La calidad de los datos de mi organización es precisa y coherente.',
      'Los datos de mi organización están bien integrados y están a disposición de los empleados con los derechos de acceso adecuados.'
    ],
    'Equipos': [
      'Hay apertura a nuevas ideas o tecnologías.',
      'Uno o más miembros del equipo han mostrado interés en aprender sobre IA',
      'Se han identificado roles que podrían beneficiarse con herramientas inteligentes.',
      'Se ha incentivado la formación básica (videos, cursos, webinars) en temas de IA.',
      'Se comunican claramente los beneficios de la IA al equipo.',
      'Se aborda el miedo al reemplazo con capacitación y transparencia.'
    ],
    'Procesos': [
      'Las actividades principales de la empresa se rigen por procesos estandarizados.',
      'Las personas involucradas conocen sus responsabilidades dentro de cada proceso.',
      'Los procesos clave están documentados (manuales, diagramas, SOPs).',
      'Existen indicadores de desempeño de procesos (KPIs Operativos).',
      'Se trabaja en identificar y automatizar procesos repetitivos de manera continua.',
      'Los procesos actuales soportarían un crecimiento significativo en volumen o complejidad.'
    ],
    'Aplicaciones iniciales': [
      'Asistentes de contenido:¿Se utilizan herramientas de IA para redactar, corregir o generar contenidos (por ejemplo, para blogs, redes sociales, presentaciones)? (Ej: ChatGPT, Jasper, Copy.ai)',
      'Chatbots y atención automática: ¿Se ha considerado implementar un chatbot en la web o redes sociales?',
      'Clasificación automática de correos o consultas:¿Se están explorando sistemas de IA que prioricen, clasifiquen o respondan correos electrónicos o solicitudes automáticamente?',
      'Análisis de datos de clientes:¿Hay interés en usar IA para analizar datos de ventas o comportamiento de clientes?',
      'Análisis de satisfacción del cliente:¿Se utilizan herramientas de IA para analizar encuestas, comentarios o reseñas de clientes y extraer insights sobre su satisfacción?',
      'Optimización de procesos internos:¿Se consideran herramientas de IA para detectar cuellos de botella o mejorar procesos administrativos o logísticos?',
      'Sistemas de recomendación de productos o servicios:¿Se explora el uso de IA para personalizar recomendaciones a clientes, basadas en su historial de compras o navegación?',
      'Detección de anomalías o fraudes:¿Se están considerando soluciones de IA para detectar transacciones inusuales, fraudes o riesgos operativos?',
      'Soporte en procesos de reclutamiento:¿Se evalúan herramientas de IA para ayudar en la preselección de candidatos o análisis de CVs?',
      'Automatización de tareas financieras básicas:¿Se utilizan sistemas de IA para conciliaciones contables, gestión de gastos o reportes automáticos?'
    ]
  };

  // Pregunta final
  const finalQuestion = {
    '¿Hay alguna tarea, proceso o problema específico en tu empresa que creas que podría mejorarse o resolverse con IA?(Podés describirlo brevemente, aunque no sepas exactamente cómo se aplicaría la IA)':
      properties['¿Hay alguna tarea, proceso o problema específico en tu empresa que creas que podría mejorarse o resolverse con IA?(Podés describirlo brevemente, aunque no sepas exactamente cómo se aplicaría la IA)']
  };

  // Calcular suma total por grupo
  const calculateGroupTotal = (groupQuestions, groupName) => {
    const scores = groupQuestions
      .map(question => {
        const value = properties[question];
        if (value) {
          const score = getPropertyValue(value);
          return isScoreQuestion(score, groupName) ? parseInt(score) : null;
        }
        return null;
      })
      .filter(score => score !== null);

    if (scores.length === 0) return 0;
    return scores.reduce((sum, score) => sum + score, 0);
  };

  // Generar datos para gráficos
  const generateChartData = () => {
    const chartData = [];

    Object.entries(questionGroups).forEach(([groupName, questions]) => {
      const total = calculateGroupTotal(questions, groupName);
      if (total > 0) {
        // Calcular el máximo posible para este grupo
        const maxPossible = groupName === 'Aplicaciones iniciales' ? questions.length * 3 : questions.length * 4;
        const average = total / questions.length; // Para el gráfico radar necesitamos el promedio
        chartData.push({
          group: groupName,
          total: total,
          maxPossible: maxPossible,
          average: average, // Para el gráfico radar
          color: getScoreColor(Math.round(average))
        });
      }
    });

    return chartData;
  };

  const chartData = generateChartData();

  // Calcular estadísticas generales
  const calculateGeneralStats = () => {
    // Calcular el promedio general basado en los totales y máximos posibles
    const totalSum = chartData.reduce((sum, item) => sum + item.total, 0);
    const maxPossibleSum = chartData.reduce((sum, item) => sum + item.maxPossible, 0);
    const totalAverage = maxPossibleSum > 0 ? ((totalSum / maxPossibleSum) * 4).toFixed(1) : 0;

    const allScores = chartData.map(item => item.average);
    const maxScore = Math.max(...allScores);
    const minScore = Math.min(...allScores);
    const strongestArea = chartData.find(item => item.average === maxScore)?.group || 'N/A';
    const weakestArea = chartData.find(item => item.average === minScore)?.group || 'N/A';

    return {
      totalAverage: parseFloat(totalAverage),
      strongestArea,
      weakestArea,
      totalAreas: chartData.length
    };
  };

  const stats = calculateGeneralStats();

  // Componente de gráfico radar usando Chart.js
  const RadarChartQuestions = ({ questions, groupName }) => {
    console.log(`🔍 RadarChartQuestions para grupo: ${groupName}`);
    console.log(`🔍 Preguntas recibidas:`, questions);

    const validQuestions = questions
      .map(question => {
        const value = properties[question];
        console.log(`🔍 Procesando pregunta: "${question.substring(0, 50)}..."`);
        console.log(`🔍 Valor encontrado:`, value);
        if (value) {
          const score = getPropertyValue(value);
          console.log(`🔍 Score extraído: ${score}`);
          const isValid = isScoreQuestion(score, groupName);
          console.log(`🔍 ¿Es válido? ${isValid}`);
          return isValid ? { question, score: parseInt(score) } : null;
        }
        console.log(`🔍 No se encontró valor para la pregunta`);
        return null;
      })
      .filter(item => item !== null);

    console.log(`🔍 Preguntas válidas finales:`, validQuestions);
    if (validQuestions.length === 0) return null;

    // Crear etiquetas cortas para el gráfico
    const labels = validQuestions.map((item, index) => `P${index + 1}`);

    // Configuración del gráfico radar
    const chartData = {
      labels: labels,
      datasets: [
        {
          label: groupName,
          data: validQuestions.map(item => item.score),
          borderColor: '#00D9C2',
          backgroundColor: 'rgba(0, 217, 194, 0.2)',
          borderWidth: 2,
          pointBackgroundColor: '#00D9C2',
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: '#00D9C2',
          pointRadius: 4,
          pointHoverRadius: 6,
        }
      ]
    };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        r: {
          beginAtZero: true,
          max: groupName === 'Aplicaciones iniciales' ? 3 : 4,
          min: 0,
          ticks: {
            stepSize: 1,
            color: '#ccc',
            font: {
              size: 12
            }
          },
          grid: {
            color: '#333'
          },
          angleLines: {
            color: '#333'
          },
          pointLabels: {
            color: '#ccc',
            font: {
              size: 10
            }
          }
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              const questionIndex = context.dataIndex;
              const question = validQuestions[questionIndex];
              return `${question.question.substring(0, 60)}... (${context.parsed.r}/${groupName === 'Aplicaciones iniciales' ? '3' : '4'})`;
            }
          }
        }
      }
    };

    return (
      <div className="radar-chart-container">
        <div className="radar-chart">
          <Radar data={chartData} options={chartOptions} />
        </div>

        {/* Leyenda de preguntas */}
        <div className="radial-legend">
          {validQuestions.map((item, index) => (
            <div key={index} className="legend-item">
              <div
                className="legend-color"
                style={{ backgroundColor: getScoreColor(item.score) }}
              ></div>
              <div className="legend-text">
                <span className="legend-score">P{index + 1}: {item.score}/{groupName === 'Aplicaciones iniciales' ? '3' : '4'}</span>
                <span 
                  className="legend-question"
                  title={item.question}
                >
                  {item.question.length > 60
                    ? item.question.substring(0, 60) + '...'
                    : item.question
                  }
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Sistema de Tabs */}
      <div className="tabs-container">
        <div className="tabs-header">
          <button
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <BarChart3 className="icon" />
            Vista General
          </button>
          {Object.keys(questionGroups).map((groupName) => {
            console.log('🔍 Renderizando tab para grupo:', groupName, 'Tipo:', typeof groupName);
            console.log('🔍 ¿Es Aplicaciones iniciales?', groupName === 'Aplicaciones iniciales');
            const getIcon = (groupName) => {
              switch (groupName) {
                case 'Negocio': return <Target className="icon" />;
                case 'Tecnología': return <Building2 className="icon" />;
                case 'Data': return <Database className="icon" />;
                case 'Equipos': return <Users className="icon" />;
                case 'Procesos': return <Shield className="icon" />;
                case 'Aplicaciones iniciales': return <Zap className="icon" />;
                default: return <BarChart3 className="icon" />;
              }
            };

            return (
              <button
                key={groupName}
                className={`tab-button ${activeTab === groupName ? 'active' : ''}`}
                onClick={() => setActiveTab(groupName)}
              >
                {getIcon(groupName)}
                {groupName}
              </button>
            );
          })}

        </div>

        <div className="tab-content">
          {/* Tab Vista General */}
          {activeTab === 'overview' && (
            <div>
              {/* Resumen de madurez digital */}
              <div className="results-summary">
                <h3><TrendingUp className="icon" /> Resumen de Madurez Digital</h3>
                <div className="summary-grid">
                  <div className="summary-item">
                    <div className="summary-value">{totalScore} / {maxPossibleScore}</div>
                    <div className="summary-label">Puntaje Total</div>
                  </div>
                  <div className="summary-item">
                    <div className="summary-value">{stats.totalAverage}/4</div>
                    <div className="summary-label">Promedio General</div>
                  </div>

                  <div className="summary-item">
                    <div className="summary-value">{stats.strongestArea}</div>
                    <div className="summary-label">Área Más Fuerte</div>
                  </div>
                  <div className="summary-item">
                    <div className="summary-value">{stats.weakestArea}</div>
                    <div className="summary-label">Área a Mejorar</div>
                  </div>
                </div>
              </div>

              {/* Cards de niveles de madurez */}
              <div className="maturity-cards-container">
                <div className="maturity-cards-title">
                  <h2>Nivel de Madurez Organizacional en IA</h2>
                </div>
                <div className="maturity-cards-grid">
                  {[1, 2, 3, 4, 5].map((level) => {
                    const getLevelInfo = (lvl) => {
                      switch (lvl) {
                        case 1: return {
                          name: 'Exploración Inicial',
                          range: '1-30',
                          description: 'La organización apenas comienza a familiarizarse con el potencial de la IA. Tiene bajo conocimiento general, sin estrategias claras ni capacidades técnicas o culturales para avanzar. Es clave sensibilizar, capacitar y generar visión común.'
                        };
                        case 2: return {
                          name: 'Compromiso Emergente',
                          range: '31-60',
                          description: 'Existen algunas iniciativas puntuales o interés general, pero sin una estrategia integrada. Hay avances parciales en infraestructura y cultura, y se empiezan a explorar casos de uso piloto. Se requiere alineación estratégica y foco en capacidades básicas.'
                        };
                        case 3: return {
                          name: 'Integración Operativa',
                          range: '61-90',
                          description: 'La IA empieza a integrarse en procesos con una visión estratégica en desarrollo. Se observa inversión en herramientas, prácticas de datos y cultura organizacional en transformación. Hay una base técnica y organizacional que permite escalar iniciativas.'
                        };
                        case 4: return {
                          name: 'Implementación Avanzada',
                          range: '91-120',
                          description: 'La IA es parte activa de la estrategia de negocio. Existen equipos dedicados, gobernanza clara, inversión sostenida, y un marco ético en marcha. La organización innova, mide resultados, y mejora continuamente en todas las dimensiones.'
                        };
                        case 5: return {
                          name: 'Liderazgo Transformacional',
                          range: '121-146',
                          description: 'La organización lidera en el uso estratégico y ético de la IA. La innovación, la cultura y la tecnología están profundamente integradas. Inspira a su industria, define benchmarks y aprovecha la IA para impulsar transformaciones sistémicas.'
                        };
                        default: return { name: '', range: '', description: '' };
                      }
                    };

                    const levelInfo = getLevelInfo(level);
                    const isActive = level === maturityLevel;

                    return (
                      <div key={level} className={`maturity-card ${isActive ? 'active' : ''}`}>
                        <div className="maturity-card-header">
                          <div className="maturity-card-number">{level}</div>
                          <div className="maturity-card-info">
                            <div className="maturity-card-name">{levelInfo.name}</div>
                            <div className="maturity-card-range">{levelInfo.range} puntos</div>
                          </div>
                        </div>
                        <div className="maturity-card-description">
                          {levelInfo.description}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <h2><BarChart3 className="icon" /> Promedio de Madurez por Área</h2>

              {/* Gráfico radar general */}
              <div className="radar-chart-container">
                <div className="radar-chart">
                  <Radar
                    data={{
                      labels: chartData.map(item => item.group),
                      datasets: [{
                        label: 'Madurez Digital',
                        data: chartData.map(item => item.average),
                        borderColor: '#00D9C2',
                        backgroundColor: 'rgba(0, 217, 194, 0.2)',
                        borderWidth: 2,
                        pointBackgroundColor: '#00D9C2',
                        pointBorderColor: '#fff',
                        pointHoverBackgroundColor: '#fff',
                        pointHoverBorderColor: '#00D9C2',
                        pointRadius: 4,
                        pointHoverRadius: 6,
                      }]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        r: {
                          beginAtZero: true,
                          max: 4,
                          min: 0,
                          ticks: {
                            stepSize: 1,
                            color: '#ccc',
                            font: { size: 12 }
                          },
                          grid: { color: '#333' },
                          angleLines: { color: '#333' },
                          pointLabels: {
                            color: '#ccc',
                            font: { size: 11 }
                          }
                        }
                      },
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          callbacks: {
                            label: function (context) {
                              const maxScale = 4;
                              return `${context.label}: ${context.parsed.r.toFixed(1)}/${maxScale}`;
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>
              </div>

              {/* Diagnóstico por Pilar */}
              <div className="diagnostico-por-pilar">
                <h2><Target className="icon" /> Diagnóstico por Pilar</h2>
                <div className="diagnostico-grid">
                  {Object.entries(diagnosticosPorNivel[maturityLevel] || {}).map(([pilar, diagnostico]) => (
                    <div key={pilar} className="diagnostico-card">
                      <div className="diagnostico-header">
                        <h3>
                          {(() => {
                            switch (pilar) {
                              case 'Negocio': return <Target className="icon" />;
                              case 'Tecnología': return <Building2 className="icon" />;
                              case 'Data': return <Database className="icon" />;
                              case 'Equipos': return <Users className="icon" />;
                              case 'Procesos': return <Shield className="icon" />;
                              case 'Aplicaciones iniciales': return <Zap className="icon" />;
                              default: return <BarChart3 className="icon" />;
                            }
                          })()}
                          {pilar}
                        </h3>
                      </div>
                      <div className="diagnostico-content">
                        <p>{diagnostico}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          )}

          {/* Tabs de cada área */}
          {Object.entries(questionGroups).map(([groupName, questions]) => {
            console.log('🔍 Verificando contenido para grupo:', groupName, 'activeTab:', activeTab);
            if (activeTab !== groupName) return null;

            const groupTotal = calculateGroupTotal(questions, groupName);
            const maxPossible = groupName === 'Aplicaciones iniciales' ? questions.length * 3 : questions.length * 4;

            return (
              <div key={groupName}>
                <h2>
                  {(() => {
                    switch (groupName) {
                      case 'Negocio': return <Target className="icon" />;
                      case 'Tecnología': return <Building2 className="icon" />;
                      case 'Data': return <Database className="icon" />;
                      case 'Equipos': return <Users className="icon" />;
                      case 'Procesos': return <Shield className="icon" />;
                      case 'Aplicaciones iniciales': return <Zap className="icon" />;
                      default: return <BarChart3 className="icon" />;
                    }
                  })()}
                  {groupName}
                  {groupTotal > 0 && (
                    <span
                      className="score"
                      style={{ backgroundColor: getScoreColor(Math.round(groupTotal / questions.length)) }}
                    >
                      {groupTotal}/{maxPossible}
                    </span>
                  )}
                </h2>

                {/* Diagnóstico específico del pilar */}
                <div className="diagnostico-especifico">
                  <div className="diagnostico-especifico-content">
                    <p>{diagnosticosPorNivel[maturityLevel]?.[groupName] || 'Diagnóstico no disponible para este pilar.'}</p>
                  </div>
                </div>

                {/* Gráfico radar de preguntas */}
                <RadarChartQuestions questions={questions} groupName={groupName} />
              </div>
            );
          })}

          {/* Tab Oportunidades */}
          {activeTab === 'opportunities' && (
            <div>
              <h2><Lightbulb className="icon" /> Oportunidades de IA</h2>
              {Object.entries(finalQuestion).map(([question, value]) => (
                <div key={question} className="question-item">
                  <div className="question-text">{question}</div>
                  <div className="question-level">{getPropertyValue(value)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Botón flotante de Jul.ia */}
      <button
        className={`juli-chatbot-btn ${showConsultorModal ? 'hidden' : ''}`}
        onClick={() => setShowConsultorModal(true)}
        title="Habla con Jul.ia - Tu consultor experto en IA"
      >
        <div className="juli-avatar">
          <div className="robot-face">
            <div className="robot-eyes">
              <div className="eye left"></div>
              <div className="eye right"></div>
            </div>
            <div className="robot-mouth"></div>
          </div>
        </div>
        <div className="chatbot-content">
          <span className="chatbot-name">¡Hablá con Jul.ia!</span>
          <span className="chatbot-subtitle">Tu consultor de IA Ready</span>
        </div>
        <div className="chatbot-pulse"></div>
      </button>

      {/* Modal del Consultor IA */}
      {showConsultorModal && (
        <div className="consultor-modal-overlay" onClick={() => setShowConsultorModal(false)}>
          <div className="consultor-modal" onClick={(e) => e.stopPropagation()}>
            <div className="consultor-modal-header">
                <div className="chat-avatar">
                  <Bot className="icon" />
                </div>
                  <h3>Jul.ia</h3>
              
              <button
                className="close-modal-btn"
                onClick={() => setShowConsultorModal(false)}
              >
                ×
              </button>
            </div>
            <div className="consultor-modal-content">
              <ChatConsultor
                data={data}
                totalScore={totalScore}
                maxPossibleScore={maxPossibleScore}
                maturityLevel={maturityLevel}
                finalQuestion={finalQuestion}
                onClearChat={() => {
                  // Función para limpiar el chat desde el modal
                  const chatStorageKey = `chat_history_${data?.id || 'default'}`;
                  localStorage.removeItem(chatStorageKey);
                  // Recargar la página para reiniciar el chat
                  window.location.reload();
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ResultsPage;
